import React from 'react';
import styled from '@emotion/styled';
import { keyframes } from '@emotion/core';
import { useSelector } from 'react-redux';

const pulseGlow = keyframes`
  0%, 100% { box-shadow: 0 0 0 0 rgba(13, 204, 138, 0.35); }
  50% { box-shadow: 0 0 0 6px rgba(13, 204, 138, 0); }
`;

const flowRight = keyframes`
  from { transform: translateX(-100%); }
  to { transform: translateX(100%); }
`;

const Strip = styled('div')`
  width: 100%;
  max-width: 520px;
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 0 0 32px 0;
`;

const Bar = styled('div')`
  display: flex;
  flex-direction: row;
  align-items: center;
  width: 100%;
`;

const StepNode = styled(({ isClickable, ...rest }) => <div {...rest} />)`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  padding: 0;
  cursor: ${(props) => (props.isClickable ? 'pointer' : 'default')};
  opacity: 1;
  transition: opacity 0.2s ease;
  flex-shrink: 0;

  &:hover {
    opacity: ${(props) => (props.isClickable ? '0.7' : '1')};
  }
`;

const Circle = styled(({ isDone, isActive, ...rest }) => <div {...rest} />)`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-family: 'Nunito Sans', sans-serif;
  font-size: 12px;
  font-weight: 800;
  flex-shrink: 0;
  transition: all 0.3s cubic-bezier(0.16, 1, 0.3, 1);

  background: ${(props) => {
    if (props.isDone) return '#0dcc8a';
    if (props.isActive) return '#2d2d44';
    return '#E0E0E0';
  }};
  color: ${(props) => {
    if (props.isDone || props.isActive) return 'white';
    return '#BDBDBD';
  }};
  box-shadow: ${(props) => {
    if (props.isDone) return '0 2px 8px rgba(13, 204, 138, 0.25)';
    if (props.isActive) return '0 2px 8px rgba(45, 45, 68, 0.2)';
    return 'none';
  }};
  animation: ${(props) => (props.isActive ? pulseGlow : 'none')} 2.5s ease infinite;
`;

const Checkmark = styled('svg')`
  width: 14px;
  height: 14px;
`;

const Label = styled(({ isDone, isActive, ...rest }) => <span {...rest} />)`
  font-family: 'Nunito Sans', sans-serif;
  font-size: 10px;
  letter-spacing: 1px;
  text-transform: uppercase;
  white-space: nowrap;
  transition: all 0.3s ease;
  color: ${(props) => {
    if (props.isDone) return '#0dcc8a';
    if (props.isActive) return '#2d2d44';
    return '#BDBDBD';
  }};
  font-weight: ${(props) => (props.isActive || props.isDone ? '800' : '600')};
`;

const ConnectorWrap = styled('div')`
  flex: 1;
  height: 2px;
  margin: 0 10px;
  margin-bottom: 20px;
  position: relative;
  overflow: hidden;
  border-radius: 1px;
`;

const ConnectorTrack = styled('div')`
  position: absolute;
  inset: 0;
  background: #E0E0E0;
  border-radius: 1px;
`;

const ConnectorFill = styled(({ isDone, ...rest }) => <div {...rest} />)`
  position: absolute;
  inset: 0;
  background: ${(props) => (props.isDone ? '#0dcc8a' : 'transparent')};
  border-radius: 1px;
  transition: background 0.5s ease;
`;

const ConnectorFlow = styled(({ isActive, ...rest }) => <div {...rest} />)`
  position: absolute;
  inset: 0;
  background: linear-gradient(90deg, transparent, rgba(13, 204, 138, 0.5), transparent);
  animation: ${flowRight} 1.5s ease-in-out infinite;
  display: ${(props) => (props.isActive ? 'block' : 'none')};
`;

const STEPS = [
  { key: 1, label: 'Upload' },
  { key: 2, label: 'Detect' },
  { key: 3, label: 'Process' },
  { key: 4, label: 'Review' },
];

// Steps are display-only — the import flow doesn't allow going back to
// previous steps (extract burns quota, processing is server-side, and the
// review-step state would be invalidated by a re-upload). Cancel actions
// elsewhere reset the wizard explicitly.
const StepProgressBar = () => {
  const currentStep = useSelector((state) => state.aiProgramImport.currentStep);

  return (
    <Strip>
      <Bar>
        {STEPS.map((step, index) => {
          const isDone = currentStep > step.key;
          const isActive = currentStep === step.key;
          const isLast = index === STEPS.length - 1;
          const isConnectorActive = currentStep === step.key + 1 && !isDone;

          return (
            <React.Fragment key={step.key}>
              <StepNode isClickable={false}>
                <Circle isDone={isDone} isActive={isActive}>
                  {isDone ? (
                    <Checkmark viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='3' strokeLinecap='round' strokeLinejoin='round'>
                      <polyline points='20 6 9 17 4 12' />
                    </Checkmark>
                  ) : step.key}
                </Circle>
                <Label isDone={isDone} isActive={isActive}>{step.label}</Label>
              </StepNode>
              {!isLast && (
                <ConnectorWrap>
                  <ConnectorTrack />
                  <ConnectorFill isDone={isDone} />
                  <ConnectorFlow isActive={isConnectorActive} />
                </ConnectorWrap>
              )}
            </React.Fragment>
          );
        })}
      </Bar>
    </Strip>
  );
};

export default StepProgressBar;
